/**
 * Tools Hub - Central tools catalog interface
 * Provides access to all diagnostic and utility tools
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material'
import { apiClient } from '../../../services/api'
import ToolCard from './components/ToolCard'
import ToolExecutor from './components/ToolExecutor'
import { generateToolUrl, toolHasPage } from '../../../config/toolsConfig'
import { getToolIcon } from './toolMeta'

interface Tool {
  id: string
  name: string
  description: string
  icon?: string
  requires_params?: boolean
  requiresInput?: boolean
  params_schema?: any
  inputSchema?: any
  last_run?: string
  lastRun?: string
  runCount?: number
  status?: string
}


const ToolsHub: React.FC = () => {
  const { fpCode: fp_code, tenureId } = useInspectorRef()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tools, setTools] = useState<Tool[] | null>(null)
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null)
  const [executorOpen, setExecutorOpen] = useState(false)

  useEffect(() => {
    if (fp_code) {
      fetchToolsCatalog()
    }
  }, [fp_code])

  const fetchToolsCatalog = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(`/inspector/${fp_code}/tools/catalog`)
      setTools(response.data || [])
    } catch (err: any) {
      console.error('Error fetching tools catalog:', err)
      setError(err.response?.data?.detail || 'Failed to load tools catalog')
      setTools([])
    } finally {
      setLoading(false)
    }
  }

  const handleToolSelect = async (tool: Tool) => {
    // Special handling for invoice payments export - direct download
    if (tool.id === 'invoice_payments_export') {
      try {
        const response = await apiClient.get(
          `/inspector/sales/tools/invoice-payments-export/${fp_code}`,
          { responseType: 'blob' }
        )

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        const contentDisposition = response.headers['content-disposition']
        let filename = `${fp_code}_invoice_inspect_${new Date().toISOString().split('T')[0]}.xlsx`
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error: any) {
        console.error('Error downloading invoice payments export:', error)
        setError(error.response?.data?.detail || 'Failed to download export')
      }
      return
    }

    if (toolHasPage(tool.id)) {
      const url = generateToolUrl(fp_code!, tool.id, tenureId)
      navigate(url)
    } else {
      setSelectedTool(tool)
      setExecutorOpen(true)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Tools Catalog
      </Typography>

      <Grid container spacing={3}>
        {tools && tools.length > 0 && tools.map((tool) => {
          const hasPage = toolHasPage(tool.id)
          const toolUrl = hasPage && fp_code ? generateToolUrl(fp_code, tool.id, tenureId) : undefined
          return (
            <Grid item xs={12} md={6} lg={4} key={tool.id}>
              <ToolCard
                tool={tool}
                icon={getToolIcon(tool.icon)}
                onExecute={() => handleToolSelect(tool)}
                hasPage={hasPage}
                toolUrl={toolUrl}
              />
            </Grid>
          )
        })}
      </Grid>

      {(!tools || tools.length === 0) && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No tools available
            </Typography>
          </CardContent>
        </Card>
      )}

      {selectedTool && (
        <ToolExecutor
          tool={selectedTool}
          fpCode={fp_code!}
          open={executorOpen}
          onClose={() => {
            setExecutorOpen(false)
            setSelectedTool(null)
          }}
          onExecutionComplete={() => {
            fetchToolsCatalog()
          }}
        />
      )}
    </Box>
  )
}

export default ToolsHub
